import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import whyClockIcon from '../../assets/icons/why-clock.svg'
import whyEyeIcon from '../../assets/icons/why-eye.svg'
import whyMapPointerIcon from '../../assets/icons/why-map-pointer.svg'
import whyPercentIcon from '../../assets/icons/why-percent.svg'
import whyPinIcon from '../../assets/icons/why-pin.svg'
import whyPlanetIcon from '../../assets/icons/why-planet.svg'
import { ButtonElement, ButtonLocation, trackButtonClick } from '../../utils/analyticsEvents'
import { Button } from '../Button/Button'
import { Icon } from '../Icon/Icon'
import { SectionHeading } from '../SectionHeading/SectionHeading'
import styles from './WhyGrid.module.scss'

interface WhyCard {
  asterisk?: number
  icon: string
  key: string
}

const CARDS: WhyCard[] = [
  { icon: whyPinIcon, key: 'nearby' },
  { icon: whyClockIcon, key: 'fast' },
  { asterisk: 2, icon: whyPlanetIcon, key: 'simple' },
  { icon: whyEyeIcon, key: 'control' },
  { icon: whyPercentIcon, key: 'profitable' },
  { asterisk: 1, icon: whyMapPointerIcon, key: 'capitals' },
]

const FOOTNOTES = [
  { asterisk: 1, key: 'milan' },
  { asterisk: 2, key: 'countries' },
]

interface WhyGridProps {
  onSend: () => void
}

const MOBILE_VISIBLE_CARDS = 3

const Asterisk = ({ count }: { count: number }) => (
  <span className={styles.asterisk}>{'*'.repeat(count)}</span>
)

export const WhyGrid = ({ onSend }: WhyGridProps) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)

  const handleToggleClick = () => {
    const willCollapse = isExpanded

    setIsExpanded((previous) => !previous)

    if (willCollapse) {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <section ref={sectionRef} className={styles.section} id="benefits">
      <div className="container">
        <SectionHeading
          action={
            <Button
              animated
              arrow
              variant="outline"
              onClick={() => {
                trackButtonClick(ButtonElement.OpenInstruction, ButtonLocation.Advantages)
                onSend()
              }}
            >
              {t('why.action')}
            </Button>
          }
          title={t('why.title')}
        />
        <ul className={styles.list}>
          {CARDS.map((card, index) => (
            <li
              key={card.key}
              className={`${styles.card}${
                index >= MOBILE_VISIBLE_CARDS && !isExpanded ? ` ${styles.collapsed}` : ''
              }`}
            >
              <img alt="" className={styles.icon} height="36" src={card.icon} width="36" />
              <div className={styles.text}>
                <h3 className={styles.title}>{t(`why.cards.${card.key}.title`)}</h3>
                <p className={styles.description}>
                  {t(`why.cards.${card.key}.description`)}
                  {card.asterisk ? <Asterisk count={card.asterisk} /> : null}
                </p>
              </div>
            </li>
          ))}
        </ul>
        <button className={styles.more} type="button" onClick={handleToggleClick}>
          {t(isExpanded ? 'why.less' : 'why.more')}
          <Icon
            className={isExpanded ? styles.chevronUp : undefined}
            name="chevron-down"
            size={16}
          />
        </button>
        <ul className={styles.footnotes}>
          {FOOTNOTES.map(({ asterisk, key }) => (
            <li key={key} className={styles.footnote}>
              <Asterisk count={asterisk} /> {t(`why.footnotes.${key}`)}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
